import SignupDto from "@/models/Restaurants/SignupDto";
import {MarketingParams} from "@/models/Subscriptions/MarketingParams";
import type { RawLocation } from "vue-router";

export const GOOGLE_PROVIDER = "google";
export const MICROSOFT_PROVIDER = "microsoft";
export const SOCIAL_LOGIN_MODE = "login";
export const SOCIAL_DIRECT_SIGNUP_MODE = "signup-direct";
export const SOCIAL_TRIAL_SIGNUP_MODE = "signup-trial";

const SOCIAL_SIGNUP_STORAGE_PREFIX = "menusano.socialSignup";
const MARKETING_PARAMS_STORAGE_KEY = "menusano.marketingParams";
const processingCallbacks = new Set<string>();

interface SocialCallbackOptions {
  provider: string;
  expectedMode: string;
  router: any;
  route: any;
  onProviderError: (message: string) => void;
  onComplete: (code: string) => Promise<boolean>;
  onFailure: (error: any) => void;
}

export function buildSocialBeginUrl(provider: string, mode: string) {
  const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);
  return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/Users/Begin${providerName}Login?mode=${encodeURIComponent(mode)}`;
}

function buildSignupStorageKey(provider: string, mode: string) {
  return `${SOCIAL_SIGNUP_STORAGE_PREFIX}.${provider}.${mode}`;
}

function getQueryKeys(provider: string) {
  return {
    mode: `${provider}Mode`,
    code: `${provider}Code`,
    error: `${provider}Error`,
  };
}

export function saveSocialSignup(provider: string, mode: string, signup: SignupDto) {
  signup.marketingParams = getMarketingParams();

  sessionStorage.setItem(
    buildSignupStorageKey(provider, mode),
    JSON.stringify(signup)
  );
}

export function getMarketingParams(): MarketingParams {
  const browserQuery = new URLSearchParams(window.location.search);
  const hashQuery = new URLSearchParams(window.location.hash.split("?")[1] || "");
  const getValue = (name: string) =>
    hashQuery.get(name) || browserQuery.get(name) || "";
  const marketingParams = {
    source: getValue("utm_source"),
    medium: getValue("utm_medium"),
    campaign: getValue("utm_campaign"),
    term: getValue("utm_term"),
    gclid: getValue("gclid"),
  };

  if (Object.values(marketingParams).some(Boolean)) {
    sessionStorage.setItem(MARKETING_PARAMS_STORAGE_KEY, JSON.stringify(marketingParams));
    return marketingParams;
  }

  try {
    return JSON.parse(sessionStorage.getItem(MARKETING_PARAMS_STORAGE_KEY) || "null") || marketingParams;
  }
  catch {
    return marketingParams;
  }
}

export function loadSocialSignup(provider: string, mode: string): SignupDto | null {
  const rawValue = sessionStorage.getItem(buildSignupStorageKey(provider, mode));
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as SignupDto;
  }
  catch {
    return null;
  }
}

export function clearSocialSignup(provider: string, mode: string) {
  sessionStorage.removeItem(buildSignupStorageKey(provider, mode));
}

export async function clearSocialAuthQuery(router: any, route: any, provider: string) {
  const query = {...route.query};
  const keys = getQueryKeys(provider);
  delete query[keys.mode];
  delete query[keys.code];
  delete query[keys.error];

  await router.replace({
    path: route.path,
    query,
  });
}

export async function clearSocialAuthQuerySafe(router: any, route: any, provider: string) {
  try {
    await clearSocialAuthQuery(router, route, provider);
  }
  catch {
    // Ignore route cleanup issues after successful authentication.
  }
}

export async function handleSocialCallback(options: SocialCallbackOptions): Promise<boolean> {
  const keys = getQueryKeys(options.provider);
  const mode = options.route.query[keys.mode] as string;
  const code = options.route.query[keys.code] as string;
  const error = options.route.query[keys.error] as string;

  if (mode !== options.expectedMode) {
    return false;
  }

  if (error) {
    options.onProviderError(error);
    await clearSocialAuthQuerySafe(options.router, options.route, options.provider);
    return false;
  }

  if (!code) {
    return false;
  }

  const callbackKey = `${options.provider}:${options.expectedMode}`;
  if (processingCallbacks.has(callbackKey)) {
    return false;
  }

  processingCallbacks.add(callbackKey);

  try {
    await clearSocialAuthQuerySafe(options.router, options.route, options.provider);
    return await options.onComplete(code);
  }
  catch (error) {
    options.onFailure(error);
    return false;
  }
  finally {
    processingCallbacks.delete(callbackKey);
  }
}

export async function navigateSafely(router: any, location: RawLocation | string) {
  try {
    await router.push(location);
  }
  catch {
    // Ignore duplicate or cancelled navigations after successful authentication.
  }
}
